import React, { useState, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

import EarnSection from './sections/EarnSection';
import SpendSection from './sections/SpendSection';
import CollectionSection from './sections/CollectionSection';
import NavigationButton from './sections/NavigationButton';

import './App.css';

axios.defaults.withCredentials = true;

const App = () => {
  const [activeSection, setActiveSection] = useState('');
  const [playNotification, setPlayNotification] = useState(false);
  const [earnNotification, setEarnNotification] = useState(false); 
  const [spendNotification, setSpendNotification] = useState(false);
  const [timeUntilNextDay, setTimeUntilNextDay] = useState(null);

  const [showSignUpPopup, setShowSignUpPopup] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpEmailError, setSignUpEmailError] = useState('');
  const [signUpUsername, setSignUpUsername] = useState('');
  const [signUpUsernameError, setSignUpUsernameError] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpPasswordError, setSignUpPasswordError] = useState('');
  const [isSignUpValid, setIsSignUpValid] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);

  const [loginError, setLoginError] = useState(''); 
  const [username, setUsername] = useState(''); 
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState({ username: '', password: '' });
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [sessionExpired, setSessionExpired] = useState(false);

  const tokenCountdownIntervalRef = useRef(null);

  const [isVerified, setIsVerified] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [showSettings, setShowSettings] = useState(false); 
  const [emailVerified, setEmailVerified] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailError, setNewEmailError] = useState('');
  const [showSendVerificationEmailPopup, setShowSendVerificationEmailPopup] = useState(false); 
  const [showChangeEmailPopup, setShowChangeEmailPopup] = useState(false);
  const [isVerificationEmailRecentlySent, setIsVerificationEmailRecentlySent] = useState(false);
  const [isSendingVerificationEmail, setIsSendingVerificationEmail] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  const [showRecoverAccountPopup, setShowRecoverAccountPopup] = useState(false); 
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverEmailError, setRecoverEmailError] = useState('');
  const [isRecoverEmailSent, setIsRecoverEmailSent] = useState(false);

  const [passwordResetToken, setPasswordResetToken] = useState('');
  const [isPasswordReset, setIsPasswordReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const handleEarnButtonClick = () => {
    setActiveSection('earn'); 
    setPlayNotification(false);
  };

  const handleSpendButtonClick = () => {
    setActiveSection('spend'); 
    setEarnNotification(false);
  };

  const handleCollectionButtonClick = () => {
    setActiveSection('collection');
    setSpendNotification(false);
  };

  const useUrlManager = (activeSection, handleEarnButtonClick, handleSpendButtonClick, handleCollectionButtonClick) => {
    // Function to handle path changes
    const handlePathChange = () => {
      const path = window.location.pathname.slice(1); // Remove leading slash
      
      switch (path) {
        case 'earn':
          handleEarnButtonClick();
          break;
        case 'spend':
          handleSpendButtonClick();
          break;
        case 'collection':
          handleCollectionButtonClick();
          break;
        default:
          // Handle default case if needed
          handleEarnButtonClick();
          break;
      }
    };

    // Handle initial URL and direct URL entry
    useEffect(() => {
      handlePathChange();
    }, []); // Empty dependency array for initial load only

    // Handle browser back/forward buttons
    useEffect(() => {
      window.addEventListener('popstate', handlePathChange);
      
      return () => {
        window.removeEventListener('popstate', handlePathChange);
      };
    }, []);

    // Handle activeSection changes
    useEffect(() => {
      if (!activeSection) return;

      const newPath = `/${activeSection}`;
      const currentUrl = new URL(window.location.href);
      
      if (currentUrl.pathname !== newPath) {
        window.history.pushState({}, '', newPath);
      }
      const titles = {
        earn: 'One Piece Power Ranker',
        spend: 'Underworld Market',
        collection: 'My Collection',
      };

      document.title = titles[activeSection] || 'One Piece Power Ranker';
    }, [activeSection]);
  };

  useUrlManager(
    activeSection,
    handleEarnButtonClick,
    handleSpendButtonClick,
    handleCollectionButtonClick
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('verifyEmailToken');

    if (token) {
      axios
        .post(`/api/accounts/verify-email/${token}/`)
        .then((response) => {
          setIsVerified(true);
        })
        .catch((err) => {
          if (err.response && err.response.data.detail === 'Email is already verified.') {
            setIsVerified(null); // Email is already verified
          } else {
            setIsVerified(false); // Handle other errors (e.g., token expired)
          }
        });
      navigate('/', { replace: true });
    }
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('resetPasswordToken');
    
    if (token) {
      axios
        .post(`/api/accounts/api/access-reset-password/${token}/`)
        .then((response) => {
          setPasswordResetToken(token);
          setIsPasswordReset(true);
        })
        .catch((err) => {
          setIsPasswordReset(false);
        });
      navigate('/', { replace: true });
    }
  }, [location.search]);

  const handleEmailVerificationPopupClose = () =>
  {
    setIsVerified(null);
  }

  const handlePasswordResetPopupClose = () =>
  {
    setPasswordResetToken('');
    setIsPasswordReset(null);
    setNewPassword('');
    setNewPasswordError('');
    setConfirmNewPassword('');
    setConfirmNewPasswordError('');
  }

  useEffect(() => {
    // Check if user is logged in by calling the username endpoint
    const checkUserLoggedIn = async () => {
      try {
        const response = await axios.get('/api/accounts/api/user/username/');
        if (response.data.username) {
          setLoggedInUser(response.data.username); // Set logged-in user if username is returned
        }
        if (response.data.token_lifetime) {
          startTokenCountdown(response.data.token_lifetime);
        }
        getUserEmail();
      } catch (error) {
        console.error('Error checking user session:', error);

        // Check if the error indicates an expired token
        if (error.response && error.response.status === 401) {
          try {
            // Refresh the token
            console.log('Refreshing token...');
            await refreshTokenIfNeeded();
  
            // Retry the API call after refreshing the token
            const response = await axios.get('/api/accounts/api/user/username/');
            if (response.data.username) {
              setLoggedInUser(response.data.username); // Set logged-in user if username is returned
            }
            if (response.data.token_lifetime) {
              startTokenCountdown(response.data.token_lifetime);
            }
            getUserEmail();
          } catch (retryError) {
            console.error('Retry after token refresh failed:', retryError);
            setLoggedInUser('');
          }
        } else {
          // Handle non-authentication-related errors
          console.error('Unexpected error:', error);
          setLoggedInUser('');
        }
      }
    };

    // Call the checkUserLoggedIn function
    checkUserLoggedIn();

    const fetchTimeUntilNextDay = async () => {
      try {
        // Attempt the initial API call
        const response = await axios.get('/api/time-until-next-day/');
        setTimeUntilNextDay(response.data.time_until_next_day);
      } catch (error) {
        console.error('Unexpected error:', error);
      }
    };
  
    fetchTimeUntilNextDay();
  
    // Setup an interval to check the countdown every second
    const intervalId = setInterval(() => {
      setTimeUntilNextDay((prevTime) => {
        if (prevTime > 0) {
          return prevTime - 1; // Decrement by 1 second
        }
        return 0; // Reset the countdown to 0
      });
    }, 1000);
  
    // Cleanup the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);  

  const startTokenCountdown = (initialLifetime) => {
    // Clear any existing interval
    if (tokenCountdownIntervalRef.current) {
      clearInterval(tokenCountdownIntervalRef.current);
    }

    // Initialize countdown
    let remainingTime = initialLifetime;

    // Create new interval
    tokenCountdownIntervalRef.current = setInterval(() => {
      remainingTime -= 1;
      if (remainingTime <= 0) {
        // Stop the interval
        clearInterval(tokenCountdownIntervalRef.current);
        tokenCountdownIntervalRef.current = null;

        handleLogout();
        setSessionExpired(true);
        openLoginPopup();
      }
    }, 1000);
  };

  useEffect(() => {
    if (loggedInUser === '') {
      // Clear the token countdown interval
      if (tokenCountdownIntervalRef.current) {
        clearInterval(tokenCountdownIntervalRef.current);
        tokenCountdownIntervalRef.current = null;
      }
    }
  }, [loggedInUser]);

  const getUserEmail = async () => {
    try {
      const response = await axios.get('/api/accounts/api/user/email/');
      if (response.data.email) {
        setUserEmail(response.data.email);
        setEmailVerified(response.data.is_email_verified);
      }
    } catch (error) {
      console.error('Error checking user email:', error);

      if (error.response && error.response.status === 401) {
        try {
          // Refresh the token
          console.log('Refreshing token...');
          await refreshTokenIfNeeded();

          const response = await axios.get('/api/accounts/api/user/email/');
          if (response.data.email) {
            setUserEmail(response.data.email);
            setEmailVerified(response.data.is_email_verified);
          }
        } catch (retryError) {
          console.error('Retry after token refresh failed:', retryError);
        }
      } else {
        // Handle non-authentication-related errors
        console.error('Unexpected error:', error);
      }
    }
  }

  const refreshTokenIfNeeded = async () => {
    try {
      await axios.post('/api/accounts/api/token/refresh/');
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleAccessRefreshTokensExpired(loggedInUser);
      throw new Error('Token refresh failed');
    }
  };

  const handleAccessRefreshTokensExpired = (loggedInUser) =>
  {
    if (loggedInUser && loggedInUser !== '')
      {
        setLoggedInUser('');
        setUserEmail(null);
        setEmailVerified(false);
        handleSettingsClose();
        setSessionExpired(true);
        openLoginPopup();
        setIsSendingVerificationEmail(false);
        setIsChangingEmail(false);
      }
  }

  // Check if time_until_next_day reaches 0 and play notification if the active section is not "Earn"
  useEffect(() => {
    if (timeUntilNextDay === 0) {
      // Reset countdown to 24 hours (86400 seconds)
      setTimeUntilNextDay(86400);
      if(activeSection !== 'earn')
      {
        setPlayNotification(true);
      }
    }
  }, [timeUntilNextDay, activeSection]);

  const handleSignUp = async () => {
    try {
      const response = await axios.post('/api/accounts/register/', {
        username: signUpUsername,
        password: signUpPassword,
        email: signUpEmail,
      });
      
      console.log('Signup successful:', response);

      await handleLoginAfterSignUp();
    } catch (error) {
      console.error('Signup failed:', error);
      closePopups();
    }
  };

  const handleLoginAfterSignUp = async () => {
    try {
      const response = await axios.post('/api/accounts/api/token/', {
        username: signUpUsername,
        password: signUpPassword,
      });
      
      setLoggedInUser(signUpUsername); // Set logged-in user on success
      getUserEmail();
      closePopups();
      startTokenCountdown(30 * 24 * 60 * 60); // 30 days in seconds
      console.log('Login after signup successful:', response);
    } catch (error) {
      console.error('Login after signup failed:', error);
      throw error;
    }
  };  

  const handleLogin = async () => {
    try {
      const response = await axios.post('/api/accounts/api/token/', {
        username,
        password,
      });
      setLoggedInUser(username); // Set the logged-in user's username
      getUserEmail();
      closePopups(); // Close the popups
      startTokenCountdown(30 * 24 * 60 * 60); // 30 days in seconds
      // Optionally, verify tokens are set in cookies
      console.log('Login successful:', response);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      const response = await axios.post('/api/accounts/api/logout/');
      setLoggedInUser(''); // Clear the logged-in user state
      setUserEmail(null);
      setEmailVerified(false);
      console.log('Logout successful:', response);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };  

  const clearNotifications = () => {
    setPlayNotification(false);
    setEarnNotification(false);
    setSpendNotification(false);
  };

  const handleSettings = () => 
  {
    setShowSettings(true);
  }

  const handleSettingsClose = () =>
  {
    setShowSettings(false);
    setNewEmail('');
    setNewEmailError('');
  }

  useEffect(() => {
    clearNotifications();
  }, [loggedInUser]);

  const handleNavigateToCollection = () => {
    setActiveSection('collection');
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'earn':
        return (
          <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
            <EarnSection setNotification={setEarnNotification} refreshTokenIfNeeded={refreshTokenIfNeeded} loggedInUser={loggedInUser}/>
          </DndProvider>
        );
      case 'spend':
        return <SpendSection setNotification={setSpendNotification} onNavigateToCollection={handleNavigateToCollection} refreshTokenIfNeeded={refreshTokenIfNeeded} loggedInUser={loggedInUser}/>;
      case 'collection':
        return <CollectionSection refreshTokenIfNeeded={refreshTokenIfNeeded} loggedInUser={loggedInUser}/>;
      default:
        return null;
    }
  };

  const openSignUpPopup = () => {
    setShowSignUpPopup(true);
    setShowLoginPopup(false);

    setLoginError('');
    setUsername('');
    setPassword('')
    setLastSubmitted({ username: '', password: '' });
  };

  const openLoginPopup = () => {
    setShowLoginPopup(true);
    setShowSignUpPopup(false);

    setSignUpEmail('');
    setSignUpEmailError('');
    setSignUpUsername('');
    setSignUpUsernameError('');
    setSignUpPassword('')
    setSignUpPasswordError('')
    setShowSignUpPassword(false);
  };

  const closePopups = () => {
    setShowSignUpPopup(false);
    setShowLoginPopup(false);

    setSessionExpired(false);

    setSignUpEmail('');
    setSignUpEmailError('');
    setSignUpUsername('');
    setSignUpUsernameError('');
    setSignUpPassword('')
    setSignUpPasswordError('')
    setShowSignUpPassword(false);

    setLoginError('');
    setUsername('');
    setPassword('')
    setLastSubmitted({ username: '', password: '' });
  };

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };  

  const validateSignUpEmail = async (value) => {
    try {
      const response = await axios.post('/api/accounts/api/email/validate', {
        email: value,
      });
  
      if (response.data.available) {
        setSignUpEmailError(''); // Clear any previous error if the email is available
      } else {
        setSignUpEmailError('This email is already in use');
      }
    } catch (error) {
      console.error('Error checking email availability:', error);
      setSignUpEmailError('Error validating email');
    }
  };

  const validateNewEmail = async (value) => {
    try {
      const response = await axios.post('/api/accounts/api/email/validate', {
        email: value,
      });
  
      if (response.data.available) {
        setNewEmailError(''); // Clear any previous error if the email is available
      } else {
        setNewEmailError('This email is already in use');
      }
    } catch (error) {
      console.error('Error checking email availability:', error);
      setNewEmailError('Error validating email');
    }
  };

  const debouncedValidateSignUpEmail = useRef(
    debounce((value) => validateSignUpEmail(value), 500)
  ).current;  

  const debouncedValidateNewEmail = useRef(
    debounce((value) => validateNewEmail(value), 500)
  ).current;  

  const validateSignUpUsername = async (value) => {  
    try {
      const response = await axios.post('/api/accounts/api/username/validate', {
        username: value,
      });
  
      if (response.data.available) {
        setSignUpUsernameError(''); // Clear any previous error if the username is available
      } else {
        setSignUpUsernameError('This username is already in use');
      }
    } catch (error) {
      console.error('Error checking username availability:', error);
      setSignUpUsernameError('Error validating username');
    }
  };  

  const debouncedValidateSignUpUsername = useRef(
    debounce((value) => validateSignUpUsername(value), 500)
  ).current;  

  const validateSignUpPassword = (value) => {
    if (!value) {
      setSignUpPasswordError('Please enter a password');
    } else if (value.length < 8 || value.length > 200) {
      setSignUpPasswordError('Passwords must be between 8 and 200 characters long');
    } else {
      setSignUpPasswordError('');
    }
  };

  const validateNewPassword = (value) => {
    if (!value) {
      setNewPasswordError('Please enter a password');
    } else if (value.length < 8 || value.length > 200) {
      setNewPasswordError('Passwords must be between 8 and 200 characters long');
    } else {
      setNewPasswordError('');
    }
  };

  const validateConfirmNewPassword = (value) => {
    if (!value || (newPassword !== value)) {
      setConfirmNewPasswordError('Passwords do not match');
    } else {
      setConfirmNewPasswordError('');
    }
  };

  const handleSignUpEmailChange = (e) => {
    const value = e.target.value;
    setSignUpEmail(value);
    const emailRegex = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})/;
    if (!value) {
      setSignUpEmailError('Please enter an email');
      return;
    }
    else if (!emailRegex.test(value)) {
      setSignUpEmailError('Invalid email');
      return;
    }
    debouncedValidateSignUpEmail(value);
  };

  const handleNewEmailChange = (e) => {
    const value = e.target.value;
    setNewEmail(value);
    const emailRegex = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})/;
    if (!value) {
      setNewEmailError('Please enter an email');
      return;
    }
    else if (!emailRegex.test(value)) {
      setNewEmailError('Invalid email');
      return;
    }
    debouncedValidateNewEmail(value);
  };

  const handleRecoverEmailChange = (e) => {
    const value = e.target.value;
    setRecoverEmail(value);
    const emailRegex = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})/;
    if (!value) {
      setRecoverEmailError('Please enter an email');
      return;
    }
    else if (!emailRegex.test(value)) {
      setRecoverEmailError('Invalid email');
      return;
    }
    setRecoverEmailError('');
  };

  const handleSignUpUsernameChange = (e) => {
    const value = e.target.value;
    setSignUpUsername(value);
    
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
  
    if (!value) {
      setSignUpUsernameError('Please enter a username');
      return;
    } else if (value.length < 3 || value.length > 20) {
      setSignUpUsernameError('Usernames can be 3 to 20 characters long');
      return;
    } else if (!usernameRegex.test(value)) {
      setSignUpUsernameError('Usernames may only contain letters, numbers, and _');
      return;
    }
    debouncedValidateSignUpUsername(value);
  };

  const handleSignUpPasswordChange = (e) => {
    const value = e.target.value;
    setSignUpPassword(value);
    validateSignUpPassword(value);
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validateNewPassword(value);
  };

  const handleConfirmNewPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmNewPassword(value);
    validateConfirmNewPassword(value);
  };

  const toggleSignUpPasswordVisibility = () => {
    setShowSignUpPassword(prevState => !prevState);
  };

  useEffect(() => {
    if (
      !signUpEmailError &&
      !signUpUsernameError &&
      !signUpPasswordError &&
      signUpEmail &&
      signUpUsername &&
      signUpPassword
    ) {
      setIsSignUpValid(true);
    } else {
      setIsSignUpValid(false);
    }
  }, [signUpEmail, signUpUsername, signUpPassword, signUpEmailError, signUpUsernameError, signUpPasswordError]);

  const handleSignUpButtonClick = async () => {
    // Disable inputs and button when showSignUpPopup is true
    if (showSignUpPopup) {
      document.querySelectorAll('.popup-input-signup, .popup-button-signup, .eye-button').forEach((element) => {
        element.disabled = true;
      });
    }

    await handleSignUp();
  };

  useEffect(() => {
    // Enable inputs and button when showSignUpPopup is false
    if (!showSignUpPopup) {
      document.querySelectorAll('.popup-input-signup, .popup-button-signup, .eye-button').forEach((element) => {
        element.disabled = false;
      });
    }
  }, [showSignUpPopup]);

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    setLoginError(''); // Clear error message on input change
  };

  const handleLoginButtonClick = async () => {
    if (!username || !password) {
      setLoginError('Username and password required');
    } else {
      // Check if current input matches the last submitted credentials
      if (username === lastSubmitted.username && password === lastSubmitted.password) {
        setIsLoading(true);

        setTimeout(() => {
          setLoginError('Incorrect username or password');
          setIsLoading(false);
        }, 300); // Wait for 0.3 second
      } else {
        setIsLoading(true);
        try {
          await handleLogin(); // Attempt to log in
        } catch (error) {
          setLoginError('Incorrect username or password'); // Set the error message on failure
          setLastSubmitted({ username, password }); 
        } finally {
          setIsLoading(false); // Stop loading animation
        }
      } 
    }
  };

  const handleForgotPasswordButtonClick = () => {
    closePopups();
    setShowRecoverAccountPopup(true);
  };

  const handleRecoverAccountClose = () =>
  {
    setShowRecoverAccountPopup(false);
    setIsRecoverEmailSent(false);
    setRecoverEmail('');
    setRecoverEmailError('');
  }

  const handleRecoverAccountButtonClick = async () => {
    if (isRecoverEmailSent)
    {
      handleRecoverAccountClose();
    }
    else
    {
      setIsRecoverEmailSent(true);
      await handleSendRecoverEmail();
    }
  };

  const handleSendRecoverEmail = async () => {
    try {
      const response = await axios.post('/api/accounts/api/request-password-reset/', {
        email: recoverEmail,
      });
    } catch (error) {
      // console.error('Error sending recover email:', error);
    }
  }

  const handleResetPasswordButtonClick = async () => {
    setIsResettingPassword(true);
    await handleResetPassword();
    setIsResettingPassword(false);
  }

  const handleResetPassword = async () => {
    try {
      const response = await axios.post(`/api/accounts/api/reset-password/`, {
        token: passwordResetToken,
        new_password: newPassword,
      });
      
      handlePasswordResetPopupClose();
      openLoginPopup();

    } catch (error) {
      console.error('Error resetting password:', error);

      if (error.response && (error.response.data.detail === 'Password reset link has expired.' || 
        error.response.data.detail === 'Invalid password reset token.')) {
        setIsPasswordReset(false);
      } else {
        setNewPassword('');
        setNewPasswordError('Invalid password')
        setConfirmNewPassword('')
        setConfirmNewPasswordError('');
      }
    }
  }

  const handleSendVerificationEmailButtonClick = async () => {
    setIsSendingVerificationEmail(true);
    await handleSendVerificationEmail();
    setIsSendingVerificationEmail(false);
  }

  const handleCloseSendVerificationEmailPopupButtonClick = () => {
    setShowSendVerificationEmailPopup(false);
  }

  const handleSendVerificationEmail = async () => {
    
    try {
      const response = await axios.post('/api/accounts/api/user/resend-verification-email/');
      setIsVerificationEmailRecentlySent(false);
      setShowSendVerificationEmailPopup(true);
    } catch (error) {
      console.error('Error sending verification email:', error);

      if (error.response && error.response.status === 401) {
        try {
          // Refresh the token
          console.log('Refreshing token...');
          await refreshTokenIfNeeded();

          const response = await axios.post('/api/accounts/api/user/resend-verification-email/');
          setIsVerificationEmailRecentlySent(false);
          setShowSendVerificationEmailPopup(true);
        } catch (retryError) {
          console.error('Retry after token refresh failed:', retryError);
          if (retryError.message !== 'Token refresh failed') {
            setIsVerificationEmailRecentlySent(true);
            setShowSendVerificationEmailPopup(true);
          }
        }
      } else {
        // Handle non-authentication-related errors
        console.error('Unexpected error:', error);
        setIsVerificationEmailRecentlySent(true);
        setShowSendVerificationEmailPopup(true);
      }
    }
  }

  const handleChangeEmailButtonClick = async () => {
    setIsChangingEmail(true);
    await handleChangeEmail();
    setIsChangingEmail(false);
  }

  const handleCloseChangeEmailPopupButtonClick = () => {
    setShowChangeEmailPopup(false);
  }

  const handleChangeEmail = async () => {
    
    try {
      const response = await axios.post('/api/accounts/api/user/change-email/', {
        new_email: newEmail,
      });
      setIsVerificationEmailRecentlySent(false);
      setShowChangeEmailPopup(true);
    } catch (error) {
      console.error('Error changing email:', error);

      if (error.response && error.response.status === 401) {
        try {
          // Refresh the token
          console.log('Refreshing token...');
          await refreshTokenIfNeeded();

          const response = await axios.post('/api/accounts/api/user/change-email/', {
            new_email: newEmail,
          });
          setIsVerificationEmailRecentlySent(false);
          setShowChangeEmailPopup(true);
        } catch (retryError) {
          console.error('Retry after token refresh failed:', retryError);
          if (retryError.message !== 'Token refresh failed') {
            setIsVerificationEmailRecentlySent(true);
            setShowChangeEmailPopup(true);
          }
        }
      } else {
        // Handle non-authentication-related errors
        console.error('Unexpected error:', error);
        setIsVerificationEmailRecentlySent(true);
        setShowChangeEmailPopup(true);
      }
    }
  }

  return (
    <div className="app-container">
      <nav className="side-nav">
        <NavigationButton 
          label="Earn" 
          isActive={activeSection === 'earn'} 
          onClick={handleEarnButtonClick} 
          showNotification={playNotification}
        />
        <NavigationButton 
          label="Spend" 
          isActive={activeSection === 'spend'} 
          onClick={handleSpendButtonClick}
          showNotification={earnNotification}
        />
        <NavigationButton 
          label="Collection" 
          isActive={activeSection === 'collection'} 
          onClick={handleCollectionButtonClick} 
          showNotification={spendNotification} 
        />
      </nav>
      <main className="main-content">
        {renderSection()}
      </main>
      <nav className="side-nav">
        {loggedInUser ? (
          <>
            <span className="nav-username">{loggedInUser}</span>
            <button onClick={handleSettings} className="nav-button">Settings</button>
            <button onClick={handleLogout} className="nav-button">Logout</button>
          </>
        ) : (
          <>
            <button onClick={openSignUpPopup} className="nav-button">Sign Up</button>
            <button onClick={openLoginPopup} className="nav-button">Log In</button>
          </>
        )}
      </nav>

      {isVerified !== null && (
        <div className="popup-overlay">
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            {isVerified ? (
              <>
                <h2 className="popup-title-email">Email Verified Successfully</h2>
                <p className="popup-text-email">Your email has been verified.</p>
                <button className="popup-button-email" onClick={handleEmailVerificationPopupClose}>Confirm</button>
              </>
            ) : (
              <>
                <h2 className="popup-title-email">Email Verification Failed</h2>
                <p className="popup-text-email">This verification link has expired.</p>
                <button className="popup-button-email" onClick={handleEmailVerificationPopupClose}>Confirm</button>
              </>
            )}
          </div>
        </div>
      )}

      {showSignUpPopup && (
        <div className="popup-overlay">
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-button" onClick={closePopups}>
              &times;
            </button>
            <h2 className="popup-title">Signup</h2>
            <div className="input-group-signup">
              <label className="input-label">Email</label>
              <input
                type="email"
                placeholder="Email"
                className={`popup-input-signup ${signUpEmailError ? 'error-border' : signUpEmail ? 'success-border' : ''}`}
                value={signUpEmail}
                onChange={handleSignUpEmailChange}
              />
              <div className="error-message-container-signup">
                {signUpEmailError && <p className="error-message">{signUpEmailError}</p>}
              </div>
            </div>
            <div className="input-group-signup">
              <label className="input-label">Username</label>
              <input
                type="text"
                placeholder="At least 3 characters"
                className={`popup-input-signup ${signUpUsernameError ? 'error-border' : signUpUsername ? 'success-border' : ''}`}
                value={signUpUsername}
                onChange={handleSignUpUsernameChange}
              />
              <div className="error-message-container-signup">
              {signUpUsernameError && <p className="error-message">{signUpUsernameError}</p>}
              </div>              
            </div>
            <div className="input-group-signup">
              <label className="input-label">Password</label>
              <div className="password-container-signup">
                <input
                   type={showSignUpPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  className={`popup-input-signup ${signUpPasswordError ? 'error-border' : signUpPassword ? 'success-border' : ''}`}
                  value={signUpPassword}
                  onChange={handleSignUpPasswordChange}
                />
                {signUpPassword && (
                  <button className="eye-button" onClick={toggleSignUpPasswordVisibility}>
                    {showSignUpPassword ? (
                      <span className="eye-icon">🙈</span>
                    ) : (
                      <span className="eye-icon">👁️</span>
                    )}
                  </button>
                )}
              </div>
              <div className = "error-message-container-signup">
                {signUpPasswordError && <p className="error-message">{signUpPasswordError}</p>}
              </div>
            </div>
            <button 
              className="popup-button-signup"
              onClick={handleSignUpButtonClick}
              disabled={!isSignUpValid}
            >
              Sign Up
            </button>
            <p className="popup-footer-account">
              Have an account?{' '}
              <span className="popup-link" onClick={openLoginPopup}>
                Log in
              </span>
            </p>
          </div>
        </div>
      )}

      {showLoginPopup && (
        <div className="popup-overlay">
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-button" onClick={closePopups}>
              &times;
            </button>
            <h2 className="popup-title">Login</h2>

            {sessionExpired && (
              <div className ="session-expired-section">
                <span className="warning-icon">⚠️</span>
                <span className="session-expired-text">Your session has expired. Please log in again.</span>
              </div>
            )}

            <div className="input-group-login">
              <input
                type="text"
                placeholder="Username"
                className="popup-input-login"
                value={username}
                onChange={handleInputChange(setUsername)}
              />
            </div>
            <div className="input-group-login">
              <input
                type="password"
                placeholder="Password"
                className="popup-input-login"
                value={password}
                onChange={handleInputChange(setPassword)} 
              />
            </div>
            <div className = "error-message-container-login">
              {loginError && <p className="error-message">{loginError}</p>}
            </div>
            <button
              className="popup-button-login"
              onClick={handleLoginButtonClick}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="loading-animation">
                  <div className="loading-square"></div>
                  <div className="loading-square"></div>
                  <div className="loading-square"></div>
                </div>
              ) : (
                'Log In'
              )}
            </button>
            <p className="popup-footer-forgot">
              <span className="popup-link" onClick={handleForgotPasswordButtonClick}>
                Forgot Password or Username?
              </span>
            </p>
            <div className="separator-line"></div>
            <p className="popup-footer-account">
              Don't have an account?{' '}
              <span className="popup-link" onClick={openSignUpPopup}>
                Sign Up
              </span>
            </p>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="popup-overlay">
          <div className="popup-content-settings" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-button" onClick={handleSettingsClose}>
              &times;
            </button>
            <h2 className="popup-title">Settings</h2>
            <div className="separator-line-settings"></div>
            <div className="email-status-container">
              <div>
                <p className="email-display">
                  Email: {userEmail 
                    ? `${userEmail[0]}${'*'.repeat(Math.min(userEmail.split('@')[0].length - 1, 7))}@${userEmail.split('@')[1]}` 
                    : ''}
                </p>
              </div>
              <div>
                {emailVerified ? (
                  <span className="email-verification-success">✔&#xfe0e; Verified</span>
                ) : (
                  <div>
                    <span className="warning-icon-settings">⚠️</span>
                    <span className="email-verification-failure"> Not Verified</span>
                  </div>
                )}
              </div>
            </div>
            
            {!emailVerified && (
              <button 
                className="popup-button-verify"
                onClick={handleSendVerificationEmailButtonClick}
                disabled={isSendingVerificationEmail}
              >
                Send Verification Email
              </button>
            )}
            
            <div className="input-group-new-email">
              <label className="input-label">New Email</label>
              <input
                type="email"
                placeholder="Enter new email"
                className={`popup-input-new-email ${newEmailError ? 'error-border' : newEmail ? 'success-border' : ''}`}
                value={newEmail}
                onChange={handleNewEmailChange}
              />
              <div className="error-message-container-new-email">
                {newEmailError && <p className="error-message">{newEmailError}</p>}
              </div>
            </div>
            <p className="change-email-info">The account email will not change until the new email has been verified.</p>
            <button 
              className="popup-button-new-email"
              onClick={handleChangeEmailButtonClick}
              disabled={newEmailError || !newEmail || isChangingEmail}
            >
              Change Email
            </button>
          </div>
        </div>
      )}

      {showSendVerificationEmailPopup && (
        <div className="popup-overlay">
          <div className="popup-content-settings" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-button" onClick={handleCloseSendVerificationEmailPopupButtonClick}>
              &times;
            </button>
            <h2 className="popup-title-email">Verify Email</h2>
            <div className="separator-line-settings"></div>
            {isVerificationEmailRecentlySent ? (
              <p className="verification-email-sent">Too many attempts to update email.<br />Please try again later.</p>
            ) : (
              <p className="verification-email-sent">Thanks! Your verification email has been sent.</p>
            )}
            <button 
              className="popup-button-verification-email"
              onClick={handleCloseSendVerificationEmailPopupButtonClick}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {showChangeEmailPopup && (
        <div className="popup-overlay">
          <div className="popup-content-settings" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-button" onClick={handleCloseChangeEmailPopupButtonClick}>
              &times;
            </button>
            <h2 className="popup-title-email">Change My Email</h2>
            <div className="separator-line-settings"></div>
            {isVerificationEmailRecentlySent ? (
              <p className="verification-email-sent">Too many attempts to update email.<br />Please try again later.</p>
            ) : (
              <p className="verification-email-sent">An email has been sent for verification.</p>
            )}
            <button 
              className="popup-button-verification-email"
              onClick={handleCloseChangeEmailPopupButtonClick}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {showRecoverAccountPopup && (
        <div className="popup-overlay">
          <div className="popup-content-settings" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-button" onClick={handleRecoverAccountClose}>
              &times;
            </button>
            <h2 className="popup-title-recover-account">
              {isRecoverEmailSent ? "Email Sent" : "Recover Account"}
            </h2>
            <div className="separator-line-settings"></div>
            
            {isRecoverEmailSent ? (
              <div className="recover-email-sent-block">
                <p className="recover-email-sent">
                  An email with instructions has been sent to you if the email was previously saved on your account.
                  <br></br>
                  Please check all your folders, including spam.
                </p>
              </div>
            ) : (
              <div className="input-group-new-email">
                <label className="input-label">Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  className={`popup-input-new-email ${recoverEmailError ? 'error-border' : recoverEmail ? 'success-border' : ''}`}
                  value={recoverEmail}
                  onChange={handleRecoverEmailChange}
                />
                <div className="error-message-container-new-email">
                  {recoverEmailError && <p className="error-message">{recoverEmailError}</p>}
                </div>
              </div>
            )}

            <button 
              className="popup-button-new-email"
              onClick={handleRecoverAccountButtonClick}
              disabled={recoverEmailError || !recoverEmail}
            >
              {isRecoverEmailSent ? "Confirm" : "Submit"}
            </button>
          </div>
        </div>
      )}

      {isPasswordReset !== null && (
        <div className="popup-overlay">
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            {isPasswordReset ? (
              <>
                <button className="popup-close-button" onClick={handlePasswordResetPopupClose}>
                  &times;
                </button>
                <h2 className="popup-title-recover-account">Reset Password</h2>
                <p className="popup-text-email">
                  Create a new password.
                  <br></br>
                  Do not use your old password.
                </p>
                <div className="input-group-reset-password">
                  <div className="password-container-signup">
                    <input
                      type="password"
                      placeholder="New Password"
                      className={`popup-input-signup ${newPasswordError ? 'error-border' : newPassword ? 'success-border' : ''}`}
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                    />
                  </div>
                  <div className = "error-message-container-signup">
                    {newPasswordError && <p className="error-message">{newPasswordError}</p>}
                  </div>
                </div>
                <div className="input-group-signup">
                  <div className="password-container-signup">
                    <input
                      type="password"
                      placeholder="Confirm New Password"
                      className={`popup-input-signup ${confirmNewPasswordError ? 'error-border' : confirmNewPassword ? 'success-border' : ''}`}
                      value={confirmNewPassword}
                      onChange={handleConfirmNewPasswordChange}
                    />
                  </div>
                  <div className = "error-message-container-signup">
                    {confirmNewPasswordError && <p className="error-message">{confirmNewPasswordError}</p>}
                  </div>
                </div>
                <button 
                  className="popup-button-new-password"
                  onClick={handleResetPasswordButtonClick}
                  disabled={newPasswordError || !newPassword || confirmNewPasswordError || !confirmNewPassword || isResettingPassword}
                >
                  Submit
                </button>
              </>
            ) : (
              <>
                <h2 className="popup-title-email">Reset Password</h2>
                <p className="popup-text-email">This password reset link has expired.</p>
                <button className="popup-button-email" onClick={handlePasswordResetPopupClose}>Confirm</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;